<template>
  <div>
    <div>
      <div v-if="!analysis.name" />
      <div v-else>
        <div
          v-if="analysis.type === listType || analysis.type === textType"
        >
          <v-row>
            <v-col
              cols="3"
              class="py-0 px-2"
            />
            <v-col
              cols="8"
              class="py-1"
            >
              <div class="d-flex flex-column">
                <VSelect
                  v-if="analysis.type === listType"
                  :value="getItemValue(analysis.value, idx)"
                  :label="analysis.name"
                  hideDetails
                  dense
                  outlined
                  clearable
                  :readonly="isReadOnly"
                  :items="analysis.options"
                  itemValue="id"
                  :itemText="itemTextFn"
                  @input="inputAnalysisRequest($event, analysis, 'listType')"
                />
                <VTextField
                  v-else-if="analysis.type === textType"
                  v-model="analysis.value"
                  :label="analysis.name"
                  :readonly="isReadOnly"
                  outlined
                  hideDetails
                  dense
                  class="py-1 mb-0"
                  @input="onInput(sampleAttribute, $event, analysisRequest, analysis, true)"
                />
              </div>
            </v-col>
          </v-row>
        </div>
        <div
          v-else
        >
          <v-row
            class="d-flex flex-row"
          >
            <v-col
              cols="3"
              class="py-0"
            >
              <VSelect
                v-model="analysis.symbole"
                class="select--symbole"
                outlined
                hideDetails
                dense
                :readonly="isReadOnly"
                :items="symboles"
                :data-cy="`symblole-${analysis.name}`"
                :disabled="isDisabled(analysis.value)"
                @input="updateAnalysisRequest(sampleAttribute, {value: analysis.value, id: analysis.sampleId }, analysisRequest, analysis, true)"
              />
            </v-col>
            <v-col
              cols="8"
              class="py-0"
            >
              <div class="d-flex flex-column">
                <VTextField
                  v-model="analysis.value"
                  class="analysis--value"
                  :placeholder="$t('labWorkOrderTable.writeAValue')"
                  :data-cy="`input-${analysis.name}`"
                  :readonly="isReadOnly"
                  dense
                  type="number"
                  hideDetails
                  @input="onInput(sampleAttribute, $event, analysisRequest, analysis, true)"
                />
                <MeasureNorm
                  :norm="analysis.norm"
                  :value="analysis.value"
                  :symbol="analysis.symbole"
                  :type="analysisRequest.type"
                  :conditionMatchError="conditionMatchError[analysis.analysisTypeId + analysis.sampleId]"
                  :data-cy="`measure-${analysis.name}`"
                />
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import MeasureNorm from '@/views/laboratory/workOrder/summary/MeasureNorm';
import analysisTypesConstant from '@/aquassist-shared/constants/types/componentAnalysisConfiguration';
import sampleTypes from '@/constants/sampleTypes';

import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';

export default {
    components: {
        VTextField,
        VSelect,
        MeasureNorm,
    },
    props: {
        isSummaryView: {
            type: Boolean,
            default: false,
        },
        updateAnalysisRequest: {
            type: Function,
            default: () => {},
        },
        sampleAttribute: {
            type: String,
            default: '',
        },
        analysisRequestStandards: {
            type: Array,
            default: () => ([]),
        },
        value: {
            type: Object,
            default: () => ({}),
        },
        isReadOnly: {
            type: Boolean,
            default: false,
        },
        conditionMatchError: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            mutableAnalysisTypes: [],
            symboles: ['=', '<', '>'],
            inputs: [],
            listType: analysisTypesConstant.TYPE_LIST,
            textType: analysisTypesConstant.TYPE_TEXT,
            analysisRequest: [],
            analysis: {},
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'teamMainLang',
            'siteLang',
            'labWorkOrder',
            'analysisRequests',
        ]),
    },
    watch: {
        value: {
            handler() {
                this.analysis = { ...this.value };
            },
            immediate: true,
        },
    },
    created() {
        this.analysisRequest = this.analysisRequests.find(aR => aR.id === this.analysis.analysisRequestId);
        if (this.analysisRequest) {
            this.setMutableAnalysisTypes();
        }
    },
    methods: {
        ...mapActions({
            reloadWorkOrderResults: 'getLabWorkOrderResults',
        }),
        // for AR we have analyses and analysisTypes , we need to relate them to have right value
        setMutableAnalysisTypes() {
            this.mutableAnalysisTypes = [...this.analysisRequest.analysisTypes];
            this.mutableAnalysisTypes.forEach(at => {
                const found = this.analysisRequest.analyses?.find(analysis => analysis.analysisTypeId === at.id);
                if (!(at.type === analysisTypesConstant.TYPE_LIST || at.type === analysisTypesConstant.TYPE_TEXT)) {
                    // eslint-disable-next-line no-param-reassign
                    at.symbole = found?.symbole || this.symboles[0];
                    // eslint-disable-next-line no-param-reassign
                    at.norm = found?.norm || this.getNorm(at.id) || {};
                }
                // eslint-disable-next-line no-param-reassign
                at.value = found?.value;
                // eslint-disable-next-line no-param-reassign
                at.options = at?.listTypeOptions?.length
                    ? at.listTypeOptions.map((op, index) => ({ ...op, id: index.toString() }))
                    : [];
                // eslint-disable-next-line no-param-reassign
                at.name = at.name || found.name;
            });
            this.inputs = this.mutableAnalysisTypes.map(analysisType => ({
                id: analysisType.id,
                hasValue: !!analysisType.value,
            }));
        },
        itemTextFn(item) {
            const value = item[this.userLang] || item[this.teamMainLang] || item[this.siteLang];
            return value;
        },
        getItemValue(selectedOption, indexArray) {
            if (!selectedOption) {
                return undefined;
            }
            const options = this.mutableAnalysisTypes[indexArray].listTypeOptions || [];
            // find the index of the selectedOption
            const index = options?.findIndex(option => option[this.userLang] === selectedOption || option[this.teamMainLang] === selectedOption);
            if (index !== -1) {
                return index.toString();
            }
            return undefined;
        },
        isDisabled(value) {
            return !value;
        },
        onInput(sampleAttribute, value, analysisRequest, analysis, flag) {
            const isWaterOrDepositWithMultipleSamples = [sampleTypes.WATER, sampleTypes.DEPOSIT].includes(analysisRequest.type)
                && analysisRequest.samples?.length > 1;
            this.updateAnalysisRequest(
                sampleAttribute,
                isWaterOrDepositWithMultipleSamples ? { value, id: analysis.sampleId } : value,
                analysisRequest, analysis, flag,
            );
            // Optimistic update
            if (value !== undefined && value !== null) {
                const index = this.inputs?.findIndex(input => input.id === analysis.id);
                if (index !== -1) {
                    this.inputs.splice(index, 1, {
                        id: this.inputs[index].id,
                        hasValue: !!value,
                    });
                }
            }
        },

        getNorm(analysisTypeId) {
            const standard = this.analysisRequestStandards.find(st => st.analysisType.id === analysisTypeId);
            return standard?.norm;
        },
        inputAnalysisRequest(_value, analysis, type = undefined) {
            let value = _value;
            let option = '';
            if (value) {
                if (type === 'listType') {
                    // "_value" is the index => we get the right value from "options" Array
                    const index = parseInt(_value, 10);
                    // eslint-disable-next-line no-underscore-dangle
                    value = analysis.options[index][this.userLang] || analysis.options[index][this.teamMainLang];
                }

                // we save the option in site lang  if there is no option in site lang we save it in main team lang
                let selectedOption = {};
                analysis.listTypeOptions.forEach(op => {
                    if (Object.values(op).includes(value)) { selectedOption = op; }
                });
                option = selectedOption[this.siteLang] || selectedOption[this.teamMainLang];
            }
            this.updateAnalysisRequest(this.sampleAttribute, option, this.analysisRequest, analysis, true);
        },
    },
};
</script>
<style lang="scss" scoped>
@import '@/assets/styles/components/_analysisValue.scss';

::v-deep .v-select__selections input {
    display: none; // to reduce the width of the block
}
.select--symbole {
    ::v-deep .v-input__append-inner {
        padding-left: 0px;
    }
    ::v-deep .v-input__icon--append, ::v-deep .v-input__icon {
        // reduce with of the icon after the symbole
        width: 15px;
        min-width: 15px;
        padding-left: 0px;
    }
    ::v-deep .v-select__selections {
        flex: none; // reduce with of the symbole
    }
    ::v-deep .v-input__slot {
        padding: 0px 6px !important;
    }
}
.analysis--value {
    ::v-deep input {
        text-align: center;
        font-size: 15px;
        font-weight: 500;
    }
}

</style>
